import Immutable from "immutable"
import swal from "sweetalert"
import BACKENDERROR from "app/static/frontend/imports/backenderror"
import { change, clearFields, destroy } from "redux-form/immutable"
import { segue } from "global-action-creators"
import * as djangioActionCreators from "shared/djangio/action-creators"

// helper functions
import { getErrorResponseText, getNestedObjectKey } from "shared/imports/sharedHelperFunctions"
import {
    prepareObjectForSubmit,
    generateBoard,
    generateSavedSearchStub,
    findDifferenceElement,
    rebuildMapFromList,
} from "shared/profiles/tracking_dashboard/helperFunctions"

// constants
import paths from "shared/imports/sharedPaths"
import swalContents, { DEFAULT_TRACKING_YEAR, ALL_YEARS } from "projects-new/tracking_dashboards/constants"
import {
    createTrackingDashboardActionDict,
    patchTrackingDashboardActionDict,
    archiveTrackingDashboardActionDict,
    duplicateTrackingDashboardActionDict,
} from "shared/profiles/tracking_dashboard/tracking-dashboard-profile-action-types"
const { BoardType } = DjangIO.app.tracking_dashboards.types

export const submitTrackingDashboard = (form) => async (values, dispatch, props) => {
    const {
        form,
        id,
        isEditing,
        shouldUseCustomFilters,
        shouldShowIssueFormSection,
        shouldShowSearchTermFormSection,
        shouldShowYearField,
    } = props

    if (isEditing) {
        try {
            const response = await dispatch(
                djangioActionCreators.resourcePatchDetail(
                    DjangIO.app.tracking_dashboards.models.TrackingDashboard,
                    patchTrackingDashboardActionDict,
                    id,
                    prepareObjectForSubmit({
                        formValues: values.get("year") === ALL_YEARS ? values.set("year", null) : values,
                        shouldShowIssueFormSection,
                        shouldShowSearchTermFormSection,
                        shouldShowYearField,
                        shouldUseCustomFilters,
                    }),
                ),
            )
            // tracking boards can silently fail validation but still
            // return a "valid" response, so escalate the error here to hit
            // normal error handling
            if (response.errorMessage) {
                throw new Error(response.errorMessage)
            }

            dispatch(segue(`${DjangIO.app.models.QuorumDataType.tracking_dashboard.profile}${id}/`))
            // on successful submit, clear the form
            dispatch(destroy(form))
        } catch (error) {
            if (
                getNestedObjectKey(error, ["response", "status"]) === 400 &&
                getErrorResponseText(error, "errorMessage")
            ) {
                await BACKENDERROR(error, {
                    icon: "error",
                    title: "Error",
                    text: getErrorResponseText(error, "errorMessage"),
                })
            } else if (getNestedObjectKey(error, ["response", "status"]) === 403) {
                await BACKENDERROR(error, swalContents.submitTrackingDashboardPermissionDenied)
            } else {
                await BACKENDERROR(error, swalContents.submitTrackingDashboardFailure)
            }
        }
    } else {
        try {
            const response = await dispatch(
                djangioActionCreators.resourcePostList(
                    DjangIO.app.tracking_dashboards.models.TrackingDashboard,
                    createTrackingDashboardActionDict,
                    prepareObjectForSubmit({
                        formValues: values.get("year") === ALL_YEARS ? values.set("year", null) : values,
                        shouldShowIssueFormSection,
                        shouldShowSearchTermFormSection,
                        shouldShowYearField,
                        shouldUseCustomFilters,
                    }),
                ),
            )
            dispatch(segue(`${DjangIO.app.models.QuorumDataType.tracking_dashboard.profile}${response.id}/`))
            // on successful submit, clear the form
            dispatch(destroy(form))
        } catch (error) {
            if (
                getNestedObjectKey(error, ["response", "status"]) === 400 &&
                getErrorResponseText(error, "errorMessage")
            ) {
                await BACKENDERROR(error, {
                    icon: "error",
                    title: "Error",
                    text: getErrorResponseText(error, "errorMessage"),
                })
            } else {
                await BACKENDERROR(error, swalContents.submitTrackingDashboardFailure)
            }
        }
    }
}

export const addBoard =
    ({ newBoard, form, change }) =>
    (dispatch, getState) => {
        const currentBoards = getState().form.getIn([form, "values", "configuration", "boards"])
        const newBoards = currentBoards.merge({ [newBoard.slug]: newBoard })

        // dispatch the change
        change("configuration.boards", newBoards)
    }

/**
 * Remove the boards in the configuration.boards with a board type of boardType
 * @name removeBoardsWithType
 * @function
 * @param {Type} argument - a description
 * @returns {Type} - Something
 */
export const removeBoardsWithType =
    ({ boardType, form, change }) =>
    (dispatch, getState) => {
        const currentBoards = getState().form.getIn([form, "values", "configuration", "boards"])
        const newBoards = currentBoards.valueSeq().filter((board) => board.get("board_type") !== boardType)
        change("configuration.boards", rebuildMapFromList(newBoards))
    }

/**
 * This function handles either adding or removing boards
 * @name updateBoards
 * @function
 * @param {Type} argument - a description
 * @returns {Type} - Something
 */
export const updateBoards =
    ({ newValues, form, ...args }) =>
    (dispatch, getState) => {
        const { fieldName, change } = args

        // diff the current and new values
        const currentBoardTypes = getState().form.getIn([form, "values", fieldName])
        const differenceElement = findDifferenceElement(currentBoardTypes, newValues)

        // we are adding
        if (newValues.size > currentBoardTypes.size) {
            // create a new board and add it to dashboard configuration
            const newBoard = generateBoard({ boardType: differenceElement })
            dispatch(addBoard({ newBoard, form, change }))

            // if we are adding an Issue Board, we need to force a change to the issue_saved_search field
            // so redux-form considers it dirty and does not clear when reinitializing the form
            if (differenceElement === BoardType.issue) {
                change("issue_saved_search", Immutable.fromJS(generateSavedSearchStub()))
            }
        } else {
            // we are removing
            dispatch(
                removeBoardsWithType({
                    boardType: differenceElement,
                    change,
                    form,
                }),
            )
        }

        // handle storing the field which keeps track of the types of boards the Tracking Project has
        change(fieldName, newValues)
    }

export const validateSearchTerms = (searchTermBoard) => {
    const searchTermErrors = []
    if (searchTermBoard && searchTermBoard.get("search_terms")) {
        searchTermBoard.get("search_terms").forEach((searchTerm, idx) => {
            if (!searchTerm) {
                searchTermErrors[idx] = "Search Term cannot be blank"
            }
        })
    }
    return searchTermErrors
}

/**
 * Validate the Search Term boards
 * @name validateSearchTermBoards
 * @function
 * @param {Type} argument - a description
 * @returns {Object} - The passed in error object, adding keys to the object if there are more errors
 */
export const validateSearchTermBoards = (immutableValues, errors) => {
    if (
        immutableValues.get("find_board_types").size &&
        immutableValues.get("find_board_types").includes(BoardType.search_term.value)
    ) {
        const searchTermBoardErrors = []
        immutableValues.get("searchTermBoards").forEach((searchTermBoard, idx) => {
            const boardErrors = {}
            // Name is required, unless we have only 1 Board (It will automatically be named)
            if (!searchTermBoard.get("name") && immutableValues.get("searchTermBoards").size > 1) {
                boardErrors.name = "Please provide a name for your Search Term Group"
            }

            const searchTermErrors = validateSearchTerms(searchTermBoard)
            if (searchTermErrors.length) {
                boardErrors.search_terms = searchTermErrors
            }

            if (Object.keys(boardErrors).length) {
                // collect the errors for this board
                searchTermBoardErrors[idx] = boardErrors
            }
        })
        // if we found any errors with this search term board
        if (searchTermBoardErrors.length) {
            errors.searchTermBoards = searchTermBoardErrors
            return { ...errors, searchTermsForSwal: "You have issue(s) with your Search Term Board(s)" }
        }
    }

    return errors
}

/**
 * Validate the values from the Tracking Dashboard form
 * @name validate
 * @function
 * @param {Object} values - form values as an object
 * @returns {Object} - errors - an empty object if there are no errors, or an object with
 *  key/value pairs, where the key corresponds to the failing field, and the value is a string
 *  to show
 */
export const validate = (immutableValues) => {
    let errors = {}
    if (!immutableValues.get("name")) {
        errors.name = "A name is required"
    }
    // make sure we have at least 1 board
    // The form keeps track of the type of boards that exist in the lists below
    if (
        !immutableValues.get("organize_board_types").size &&
        !immutableValues.get("find_board_types").size &&
        !immutableValues.get("analyze_board_types").size
    ) {
        const errorString = `Your Tracking Board needs at least 1 Tab`
        errors.find_board_types = errorString
    }

    // if we aren't using custom filters, we need a year
    if (!immutableValues.get("use_custom_filters") && !immutableValues.get("year")) {
        errors.year = "You must select a Year if Single Year is selected"
    }

    if (
        immutableValues.get("searchTermCustomSelection") &&
        (!immutableValues.get("reviewedSafedSearch") || !immutableValues.get("reviewedSafedSearch").size)
    ) {
        errors.reviewedSafedSearch =
            "You must choose Custom Selection Filters if you choose to use a Custom Selection Tracking Workflow"
    }

    if (
        immutableValues.get("useCustomIssuesSelection") &&
        (!immutableValues.get("issue_saved_search") || !immutableValues.get("issue_saved_search").size)
    ) {
        errors.issue_saved_search =
            "You must choose a selection of Issues if you choose to use a Custom Selection for your Issues"
    }

    // validate search term boards
    errors = validateSearchTermBoards(immutableValues, errors)

    return errors
}

export const warning = (values) => ({})

// exclude two out of the 3 board type error keys since we show use these to show errors
// on the form
const excludeKeys = ["organize_board_types", "analyze_board_types"]

/**
 * Function that gets called when form validation fails
 * @name validationFailed
 * @function
 * @param {Object} errors - an Object of key/value pairs where the value is the error string
 * Process some of the errors and exclude from swal shown to user
 */
export const validationFailed = (errors) => {
    const errorStrings = []
    Object.entries(errors).forEach(([key, err]) => {
        if (!excludeKeys.includes(key) && !key.includes("searchTermBoards")) {
            errorStrings.push(err)
        }
    })
    return swal({
        icon: "error",
        title: "Error",
        text: `Please correct the following errors: \n ${errorStrings.join("\n")}`,
    })
}

export const changeShouldUseCustomFilters =
    ({ formName, shouldUseCustomFilters, dashboardDataType }) =>
    (dispatch) => {
        if (shouldUseCustomFilters) {
            // if we are a bill or regulation tracking project, we need to clear the year field
            if (
                dashboardDataType === DjangIO.app.models.QuorumDataType.bill.value ||
                dashboardDataType === DjangIO.app.models.QuorumDataType.regulation.value
            ) {
                // signature: form:String, keepTouched: boolean, persistentSubmitErrors: boolean, ...fields:String
                dispatch(clearFields(formName, false, false, "year"))
            }
        } else {
            // we are clearing the select All vs use the Searchify filters for global filters
            // if we are a bill or regulation tracking project, we need to set the year field to an initial value
            if (
                dashboardDataType === DjangIO.app.models.QuorumDataType.bill.value ||
                dashboardDataType === DjangIO.app.models.QuorumDataType.regulation.value
            ) {
                dispatch(change(formName, "year", DEFAULT_TRACKING_YEAR()))
            } else {
                dispatch(change(formName, "global_saved_search", {}))
            }
        }

        // in all cases, we set the use_custom_filters field
        dispatch(change(formName, "use_custom_filters", shouldUseCustomFilters))
    }

/**
 * This function gets called upon submitting Searchify on seguing back to the Tracking Dashboard form
 * @name trackingDashboardFormChangeSearchify
 * @function
 * This function takes 1 object of named arguments
 * @param {Object} searchifyFilters - k/v pairs of searchify filters
 * @param {String} formName - the Tracking Dashboard form name in the store
 * @param {String} formFieldName - the field of the form to change. Supports both editing the
 * issue_saved_search, global_saved_search, and reviewedSafedSearch fields from searchify
 */
export const trackingDashboardFormChangeSearchify =
    ({ searchifyFilters, formName, formFieldName }) =>
    (dispatch) => {
        const safedSearch = Immutable.fromJS({
            count: searchifyFilters.searchifyCount,
            added_ids: searchifyFilters.addedIds,
            deleted_ids: searchifyFilters.deletedIds,
            filters: searchifyFilters.querysetList,
        })
        dispatch(change(formName, formFieldName, safedSearch))
    }

/**
 * This function gets called upon changing useCustomIssuesSelection field
 * @name changeUseCustomIssuesSelection
 * @function
 * This function takes 1 object of named arguments
 * @param {Boolean} useCustomIssueSelection - new value to set for useCustomIssuesSelection
 * @param {String} formName - the Tracking Dashboard form name in the store
 * @param {Number} allIssuesCount - count of all issues available to a user
 * This function sets a saved search which will return all Issues for issue_saved_search
 * or clears the stored saved search for issue_saved_search as well as updating useCustomIssuesSelection
 * with the new value.
 */
export const changeUseCustomIssuesSelection =
    ({ useCustomIssueSelection, formName, allIssuesCount }) =>
    async (dispatch) => {
        dispatch(change(formName, "useCustomIssuesSelection", useCustomIssueSelection))
        if (!useCustomIssueSelection) {
            // This is the case when the user selects All Issues under "How would like to select your issues?"
            await dispatch(
                trackingDashboardFormChangeSearchify({
                    searchifyFilters: {
                        addedIds: [],
                        deletedIds: [],
                        searchifyCount: allIssuesCount,
                        querysetList: [{ archived: false }],
                    },
                    formName,
                    formFieldName: "issue_saved_search",
                }),
            )
        } else {
            // This is the case when the user selects Custom Selection
            await dispatch(change(formName, "issue_saved_search", null))
        }
    }

/**
 * This function gets called upon changing searchTermUseReview field
 * @name changeSearchTermUseReview
 * @function
 * This function takes 1 object of named arguments
 * @param {Boolean} newValue - new value to set for searchTermUseReview
 * @param {String} formName - the Tracking Dashboard form name in the store
 * This function clears searchTermReviewOption if searchTermUseReview is unchecked
 * If searchTermUseReview is checked, then the default value of reviewed by me is set for
 * searchTermReviewOption.
 */
export const changeSearchTermUseReview =
    ({ searchTermUseReviewValue, formName }) =>
    (dispatch) => {
        dispatch(change(formName, "searchTermUseReview", searchTermUseReviewValue))
        if (!searchTermUseReviewValue.size) {
            // clear searchTermReviewOption
            dispatch(change(formName, "searchTermReviewOption", null))
        } else {
            // set searchTermReviewOption to be reviewed by me
            dispatch(
                change(
                    formName,
                    "searchTermReviewOption",
                    DjangIO.app.tracking_dashboards.types.SearchTermBoardReviewType.reviewed_by_me.value,
                ),
            )
        }
    }

/**
 * This function gets called upon changing reviewedTeam field
 * @name changeReviewedTeam
 * @function
 * This function takes 1 object of named arguments
 * @param {Array} reviewedTeamValue - object representing the selected option from TeamSelect
 * @param {String} formName - the Tracking Dashboard form name in the store
 * This updates the values of reviewedTeam and limited_to_teams in the store to keep them consistent.
 * reviwedTeams is a single Team ID while limited_to_teams is an array of resource_uris for teams.
 */
export const changeReviewedTeam =
    ({ reviewedTeamValue, formName }) =>
    (dispatch) => {
        dispatch(change(formName, "reviewedTeam", reviewedTeamValue && reviewedTeamValue.id))
        dispatch(change(formName, "limited_to_teams", reviewedTeamValue && [reviewedTeamValue.resource_uri]))
    }

/**
 * Delete a Tracking Dashboard and segue back to Tracking Dashboard table
 * @param   {number} id - the id of the issue to be archived
 */
export const archiveTrackingDashboard = (id) => async (dispatch) => {
    const shouldArchive = await swal(swalContents.archiveDashboard)
    if (!shouldArchive) {
        return
    }
    await dispatch(
        djangioActionCreators.resourcePatchDetail(
            DjangIO.app.tracking_dashboards.models.TrackingDashboard,
            archiveTrackingDashboardActionDict,
            id,
            { archived: true },
        ),
    )
    swal.close()
    dispatch(segue(paths.trackingDashboards))
}

// send a request to the backend to duplicate the sheet
export const duplicateTrackingDashboard = (
    trackingDashboardId,
    name, // eslint-disable-line camelcase
) =>
    djangioActionCreators.sendAction(
        DjangIO.app.tracking_dashboards.models.TrackingDashboard,
        duplicateTrackingDashboardActionDict,
        {
            action: "duplicate_tracking_dashboard",
            method: "post",
            kwargs: {
                trackingDashboardId,
                name,
            },
            payload: {
                originalTrackingDashboard: Number(trackingDashboardId),
                name,
            },
        },
    )

export const improveSearchTermsWithAI = async (query) => {
    const fetchExpandedSearch = async ({ retry }) => {
        try {
            const response = await fetch(`/magic_search/expand?search_term=${query}`, {
                method: "GET",
                credentials: "include",
            })

            if (!response.ok) {
                throw new Error()
            }

            return await response.json()
        } catch (err) {
            if (retry) {
                return await fetchExpandedSearch({ retry: false })
            }

            throw new Error(err)
        }
    }

    try {
        const response = await fetchExpandedSearch({ retry: true })

        return response
    } catch (err) {
        throw new Error(err)
    }
}
